import { useVuelidate } from "@vuelidate/core";
import { helpers, minValue } from "@vuelidate/validators";
import { format } from "date-fns";
import type { Ref } from "vue";
import { computed } from "vue";
import { EVENTS_STATUS } from "@/constants.js";
import type { DateTimeState, OccurrenceModelValue } from "../types";

export const useOccurrenceValidation = (
  occurrence: Ref<OccurrenceModelValue>,
  priceStudent: Ref<number>,
  statusId: Ref<string | undefined>,
  startDateTime: DateTimeState,
  endDateTime: DateTimeState,
  registrationDateTime: DateTimeState,
  timeZone: Ref<Record<string, any> | undefined>,
) => {
  const dateNotInPast = helpers.withMessage("Please enter a date in the future", () => {
    if (!startDateTime.date && !startDateTime.time) return true;
    const today = format(new Date(), "yyyy-MM-dd");
    if (startDateTime.date && !startDateTime.time) {
      return startDateTime.date >= today;
    }
    return format(new Date(startDateTime.date + "T" + startDateTime.time), "yyyy-MM-dd") >= today;
  });

  const dateTimeNotInPast = helpers.withMessage("Please enter a time in the future", () => {
    if (!startDateTime.time) return true;
    const now = new Date();
    let today: string;
    if (timeZone?.value?.offSetInMinutes) {
      const getOffset = now.getTimezoneOffset();
      const offsetDifference = timeZone.value.offSetInMinutes + getOffset;
      const adjustedDate = new Date(now.getTime() + offsetDifference * 60000);
      today = format(adjustedDate, "yyyy-MM-dd HH:mm");
    } else {
      today = format(now, "yyyy-MM-dd HH:mm");
    }
    return format(new Date(startDateTime.date + "T" + startDateTime.time), "yyyy-MM-dd HH:mm") >= today;
  });

  const startTimeRequired = helpers.withMessage("Add a time for the start date", (): boolean => {
    if (!startDateTime.date) return true;
    return !!startDateTime.time;
  });

  const endDateNotBeforeStartDate = helpers.withMessage("End date cannot be before the start date", () => {
    if (!endDateTime.date) return true;
    return endDateTime.date >= startDateTime.date;
  });

  const endDateTimeNotBeforeStartDate = helpers.withMessage("End date time cannot be before the start date", () => {
    if (!startDateTime.date || !endDateTime.date) return true;
    if (startDateTime.time && endDateTime.time) {
      return (
        new Date(endDateTime.date + "T" + endDateTime.time) >= new Date(startDateTime.date + "T" + startDateTime.time)
      );
    }
    return new Date(endDateTime.date) >= new Date(startDateTime.date);
  });

  const endTimeRequired = helpers.withMessage("Add a time for the end date", (): boolean => {
    if (!endDateTime.date) return true;
    return !!endDateTime.time;
  });

  const priceNotNegative = helpers.withMessage("Price can't be a negative value", () => {
    return priceStudent.value >= 0;
  });

  const priceRequiredWhenActive = helpers.withMessage("Price is required", (): boolean => {
    if (statusId.value !== EVENTS_STATUS.active.id) return true;
    return !!priceStudent.value;
  });

  const minCapacityLessThanMax = helpers.withMessage("Minimum capacity can't exceed maximum capacity", (): boolean => {
    if (occurrence.value.min_capacity == null || occurrence.value.max_capacity == null) return true;
    return occurrence.value.min_capacity <= occurrence.value.max_capacity;
  });

  const maxCapacityGreaterThanMin = helpers.withMessage(
    "Maximum capacity can't be lower than the minimum capacity",
    (): boolean => {
      if (occurrence.value.max_capacity == null || occurrence.value.min_capacity == null) return true;
      return occurrence.value.max_capacity >= occurrence.value.min_capacity;
    },
  );

  const registrationTimeRequired = helpers.withMessage("Add a time for the registration deadline", (): boolean => {
    if (!registrationDateTime.date) return true;
    return !!registrationDateTime.time;
  });

  const registrationTimeZoneRequired = helpers.withMessage(
    "Select a time zone for the registration deadline",
    (): boolean => {
      if (!registrationDateTime.date) return true;
      return !!timeZone.value;
    },
  );

  const rules = computed(() => ({
    occurrence: {
      price_student: {
        priceRequiredWhenActive,
        priceNotNegative,
      },
      start_date_time: { dateNotInPast },
      start_time: { dateTimeNotInPast, startTimeRequired },
      end_date_time: { endDateNotBeforeStartDate },
      end_time: { endDateTimeNotBeforeStartDate, endTimeRequired },
      min_capacity: { minCapacityLessThanMax, minValue: minValue(0) },
      max_capacity: { maxCapacityGreaterThanMin, minValue: minValue(0) },
      registration_deadline: { registrationTimeRequired },
      time_zone: { registrationTimeZoneRequired },
    },
  }));

  const v$ = useVuelidate(
    rules,
    { occurrence },
    {
      $registerAs: "OccurrenceModal",
      $lazy: true,
    },
  );

  return { v$ };
};
